import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Buttons from "../../../components/Buttons/Buttons";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import {
  GetSurveyHistoryByPatientId,
  GetSurveySubmissionById,
} from "../../../utils/apiCalls/surveyBuilder";
import { ASSIGNMENT_STATUS_CHIP_CONFIG } from "../../../utils/surveysConstants";
import { useToast } from "../../../utils/ToastContext";

const SURVEY_TABLE_COLUMN_COUNT = 5;

const SurveyTab = ({ patientId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const fetchSurveyHistory = async (id, isStale = () => false) => {
    setIsLoading(true);
    setFetchError("");
    try {
      const res = await GetSurveyHistoryByPatientId(id);
      if (isStale()) return;
      if (res.statusCode === 200) {
        setRows(res.data);
      } else if (res.statusCode === 204) {
        setRows([]);
      } else {
        setRows([]);
        setFetchError(res.message || "Failed to load survey history");
      }
    } catch (error) {
      if (isStale()) return;
      setRows([]);
      setFetchError(error?.message || "Failed to load survey history");
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    fetchSurveyHistory(patientId, () => ignore);
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const retryFetch = () => fetchSurveyHistory(patientId);

  const handleView = async (row) => {
    try {
      const res = await GetSurveySubmissionById(row.id);
      if (res.statusCode === 200) {
        sessionStorage.setItem("surveyResultRowData", JSON.stringify(res.data));
        // "from: patient-survey" tells the detail page's back button to
        // return here (the patient's Survey tab) instead of the survey's
        // own results list — see SurveyResultDetailPage's handleBack.
        sessionStorage.setItem(
          "surveyResultsContext",
          JSON.stringify({ surveyId: row.surveyId, surveyTitle: row.surveyTitle, from: "patient-survey" }),
        );
        navigate("/survey-results-view", {
          state: {
            result: res.data,
            surveyId: row.surveyId,
            surveyTitle: row.surveyTitle,
            from: "patient-survey",
          },
        });
      } else {
        showToast(res.message || "Failed to load survey submission", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to load survey submission", "error");
    }
  };

  return (
    <div className="tab-panel-content">
      <div className="tab-panel-header">
        <h3>Survey</h3>
      </div>
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Survey</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableStatusRow
                colSpan={SURVEY_TABLE_COLUMN_COUNT}
                loading={isLoading}
                error={fetchError}
                onRetry={retryFetch}
                emptyMessage="No surveys assigned to this patient"
              />
            ) : (
              rows.map((row) => {
                const chip = ASSIGNMENT_STATUS_CHIP_CONFIG[(row.statusName || "").toLowerCase()] || {
                  label: row.statusName,
                  color: "default",
                };
                return (
                  <TableRow key={row.id}>
                    <TableCell>{row.surveyTitle}</TableCell>
                    <TableCell>{row.assignedAt ? new Date(row.assignedAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>{row.completedAt ? new Date(row.completedAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <Chip label={chip.label} color={chip.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Buttons
                        text="View"
                        type="button"
                        variant="smallsecondary"
                        onClick={() => handleView(row)}
                        disabled={!row.completedAt}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SurveyTab;
